import cloneDeep from 'lodash.clonedeep';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {useNavigate, useParams} from 'react-router-dom';

import {
  getFeedbackData,
  RIR_TARGET_REPS_BY_MESO_WEEKS,
  STATUSES,
  STATUSES_FINISHED,
  TRAINING_EVENTS,
} from '../../../../lib/training.mjs';
import {
  useMesocycle,
  useTrackEvent,
  useUpdateDay,
  useUpdateDayBodyweight,
  useUpdateMeso,
  useUpdateMesoProgressions,
} from '../api.js';
import {MOBILE_HEADER_PORTAL_NODE} from '../constants.js';
import {useBreakpoint, useExercisesById, useMutationCallback} from '../utils/hooks.js';
import {dayExerciseRequiresBodyweight, getFirstDayInProgress} from '../utils/index.js';
import {runAfterAnimations} from '../utils/misc.js';
import useLocalStore from '../utils/useLocalStore.js';
import DeleteMesoDayExerciseDialog from './DeleteMesoDayExerciseDialog.jsx';
import FeedbackDialog from './FeedbackDialog.jsx';
import MesoDayExercise from './MesoDayExercise.jsx';
import MesoDayHeader from './MesoDayHeader.jsx';
import MesoDayReadynessDialog from './MesoDayReadynessDialog.jsx';
import MesoSummaryDialog from './MesoSummaryDialog.jsx';
import {PostMesoDialog} from './PostMesoDialog.jsx';
import PostWorkoutDialog from './PostWorkoutDialog.jsx';
import ProgressionsDialog from './ProgressionsDialog.jsx';
import RepsInfoDialog from './RepsInfoDialog.jsx';
import ConfirmDialog from './ui/ConfirmDialog.jsx';
import Page from './ui/Page.jsx';

const MesoDay = () => {
  const {mesoKey, week, day: dayParam} = useParams();
  const {data: meso} = useMesocycle(mesoKey);
  const exercisesById = useExercisesById();
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();

  const day = meso.weeks[week - 1].days[dayParam - 1];

  const finishWorkout = useUpdateDay(day.id, {meta: {action: 'finishWorkout'}});
  const finishMeso = useUpdateMeso(mesoKey, {meta: {action: 'finishMeso'}});

  const resetDayOpen = useLocalStore(st => st.resetDayOpen);
  const setResetDayOpen = useLocalStore(st => st.setResetDayOpen);
  const setReadynessDialogOpen = useLocalStore(st => st.setReadynessDialogOpen);
  const isFeedbackOpen = useLocalStore(st => st.isFeedbackOpen);
  const setIsFeedbackOpen = useLocalStore(st => st.setIsFeedbackOpen);
  const skipMesoDayOpen = useLocalStore(st => st.skipMesoDayOpen);
  const setSkipMesoDayOpen = useLocalStore(st => st.setSkipMesoDayOpen);
  const endMesoOpen = useLocalStore(st => st.endMesoOpen);
  const setEndMesoOpen = useLocalStore(st => st.setEndMesoOpen);
  const isMesoSummaryOpen = useLocalStore(st => st.isMesoSummaryOpen);
  const setIsMesoSummaryOpen = useLocalStore(st => st.setIsMesoSummaryOpen);

  const [progressionsOpen, setProgressionsOpen] = useState(false);
  const [dayExerciseToDelete, setDayExerciseToDelete] = useState();
  const [hasUserDismissed, setHasUserDismissed] = useState(false);
  const [finishWorkoutDialogOpen, setFinishWorkoutDialogOpen] = useState(false);
  const [postMesoDialogOpen, setPostMesoDialogOpen] = useState(false);

  const shouldShowButton =
    meso.status === STATUSES.meso.pendingConfirmation && !finishWorkoutDialogOpen;
  const shouldShowFinishWorkoutButton =
    day.status === STATUSES.day.pendingConfirmation && !finishWorkoutDialogOpen;
  const isInMeso = Array.isArray(meso.weeks);

  const dayInProgress = useMemo(() => {
    if (!isInMeso) {
      return false;
    }
    return getFirstDayInProgress(meso);
  }, [isInMeso, meso]);

  const isSameDay =
    dayInProgress &&
    dayInProgress.week + 1 === Number(week) &&
    dayInProgress.position + 1 === Number(dayParam);

  const handleEndMesoClose = useMutationCallback(() => {
    setEndMesoOpen(false);
  }, [setEndMesoOpen]);

  const handleLink = useCallback(() => {
    if (!isInMeso || !dayInProgress) {
      return;
    }
    setEndMesoOpen(false);
    navigate(
      `/mesocycles/${meso.key}/weeks/${dayInProgress.week + 1}/days/${dayInProgress.position + 1}`
    );
  }, [dayInProgress, isInMeso, meso.key, navigate, setEndMesoOpen]);

  const handleFinishMeso = useMutationCallback(() => {
    finishMeso.mutate(
      {finishedAt: new Date().toISOString()},
      {
        onSuccess: () => {
          trackEvent.mutate({
            event: TRAINING_EVENTS.TRAINING_MESO_FINISHED_AT,
            at: new Date().toISOString(),
            mesoKey,
          });

          runAfterAnimations(() => {
            setFinishWorkoutDialogOpen(false);
            runAfterAnimations(() => {
              setPostMesoDialogOpen(true);
            });
          });
        },
      }
    );
  }, [finishMeso, mesoKey, trackEvent]);

  const handleFinishWorkout = useMutationCallback(() => {
    // A day must have a pendingConfirmation status before it can be finished
    if (day.status !== STATUSES.day.pendingConfirmation) {
      const err = new Error('trying to finish a day that isnt pending confirmation');
      err.extraData = {mesoDay: JSON.parse(JSON.stringify(day))};
      throw err;
    }

    const newDay = {...day};
    newDay.finishedAt = new Date().toISOString();

    finishWorkout.mutate(newDay, {
      onSuccess: () => {
        trackEvent.mutate({
          event: TRAINING_EVENTS.TRAINING_DAY_FINISHED_AT,
          at: new Date().toISOString(),
          mesoKey: meso.key,
          dayId: newDay.id,
        });

        runAfterAnimations(() => setFinishWorkoutDialogOpen(true));
      },
    });
  }, [day, finishWorkout, meso.key, trackEvent]);

  // TODO: See if we can do away with this
  const [initialized, setInitialized] = useState(false);

  const isDesktop = useBreakpoint('desktop');

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
    }
  }, [initialized]);

  const [feedbackDialogData, setFeedbackDialogData] = useState(null);

  // dayExercises that require bodyweight
  const bodyweightDexes = useMemo(
    () =>
      day.exercises?.filter(
        dayExercise => dayExerciseRequiresBodyweight(dayExercise, exercisesById) && dayExercise
      ),
    [day?.exercises, exercisesById]
  );

  const resetDay = useUpdateDay(day.id, {meta: {action: 'resetDay'}});
  const skipDay = useUpdateDay(day.id, {meta: {action: 'skipDay'}});
  const updateBodyweight = useUpdateDayBodyweight(day.id);
  const updateProgressions = useUpdateMesoProgressions(meso.key);

  // When resetting a day
  const handleCloseResetDay = useMutationCallback(() => setResetDayOpen(false));
  const handleResetDay = useMutationCallback(() => {
    const newExercises = cloneDeep(day.exercises);
    for (const exercise of newExercises) {
      for (const set of exercise.sets) {
        if (set.finishedAt) {
          set.finishedAt = null;
        }
      }
    }
    resetDay.mutate({...day, exercises: newExercises}, {onSuccess: handleCloseResetDay});
  }, [day, handleCloseResetDay, resetDay]);

  // When skipping a day
  const handleCloseSkipDay = useMutationCallback(() => setSkipMesoDayOpen(false));
  const handleSkipDay = useMutationCallback(() => {
    const newExercises = cloneDeep(day.exercises);
    for (const exercise of newExercises) {
      for (const set of exercise.sets) {
        // We need to decide which sets we're finishing vs which ones we're skipping
        // I have a sense that if they have saved reps actual but not "clicked save"
        // then did they really do the set? should we overwite the reps actual value they chose?
        if (!set.finishedAt) {
          set.repsActual = -1;
          set.finishedAt = new Date().toISOString();
        }
      }
    }
    skipDay.mutate({...day, exercises: newExercises}, {onSuccess: handleCloseSkipDay});
  }, [day, handleCloseSkipDay, skipDay]);

  const handleCloseBodyweight = useMutationCallback(() => setReadynessDialogOpen(false));
  const handleUpdateBodyweight = useMutationCallback(
    (weight, unit) => {
      updateBodyweight.mutate(
        {bodyweight: parseInt(weight), unit},
        {onSuccess: handleCloseBodyweight}
      );
    },
    [handleCloseBodyweight, updateBodyweight]
  );

  // We always have a reference to a muscle group that requires feedback.
  const requiredFeedbackData = useMemo(() => {
    return !day.finishedAt ? getFeedbackData(day) : null;
  }, [day]);

  const handleOpenRequiredFeedback = useCallback(() => {
    setFeedbackDialogData(requiredFeedbackData);
    setIsFeedbackOpen(true);
  }, [requiredFeedbackData, setIsFeedbackOpen]);

  const handleOpenMuscleGroupOnly = useCallback(
    dmg => {
      setFeedbackDialogData({
        dayMuscleGroup: dmg,
        needsJointPainDex: null,
        needsSoreness: true,
        needsPump: true,
        needsWorkload: true,
      });
      setIsFeedbackOpen(true);
    },
    [setIsFeedbackOpen]
  );

  const handleCloseFeedback = useCallback(
    hasUserDismissed => {
      setHasUserDismissed(hasUserDismissed);
      setIsFeedbackOpen(false);
      runAfterAnimations(() => {
        setFeedbackDialogData(null);
      });
    },
    [setIsFeedbackOpen]
  );

  // Trigger close feedback modal on day change.
  // Must be before the useEffect that opens on day load
  useEffect(() => {
    const feedbackDialogDataDayId = feedbackDialogData?.dayMuscleGroup?.dayId;
    if (feedbackDialogDataDayId && feedbackDialogDataDayId !== day.id) {
      handleCloseFeedback(false);
    }
  }, [day.id, feedbackDialogData, handleCloseFeedback]);

  // Trigger open feedback modal if user hasn't already dismissed it once.
  // Must be after the useEffect that closes the feedback modal on day change
  useEffect(() => {
    if (
      !isFeedbackOpen &&
      !hasUserDismissed &&
      !feedbackDialogData &&
      day.status === STATUSES.day.pendingFeedback
    ) {
      handleOpenRequiredFeedback();
    }
  }, [
    day.status,
    feedbackDialogData,
    handleOpenRequiredFeedback,
    hasUserDismissed,
    isFeedbackOpen,
  ]);

  // Reset user dismissed status if the day states changes away from pending feedback
  useEffect(() => {
    if (day.status !== STATUSES.day.pendingFeedback && hasUserDismissed) {
      setHasUserDismissed(false);
    }
  }, [day.status, hasUserDismissed]);

  const targetRIR = Math.abs(RIR_TARGET_REPS_BY_MESO_WEEKS[meso.weeks.length][day.week]);

  const node = document.getElementById(MOBILE_HEADER_PORTAL_NODE);

  return (
    <Page disablePadding>
      <div className="pt-4">
        {initialized &&
          !!node &&
          !isDesktop &&
          createPortal(
            <MesoDayHeader
              bodyweightDexes={bodyweightDexes}
              setProgressionsOpen={setProgressionsOpen}
            />,
            node
          )}

        {isDesktop && (
          <div className="sticky top-0 z-30 mb-4">
            <MesoDayHeader
              bodyweightDexes={bodyweightDexes}
              setProgressionsOpen={setProgressionsOpen}
            />
          </div>
        )}

        {day.exercises.map(dex => (
          <MesoDayExercise
            key={dex.id}
            day={day}
            dayExercise={dex}
            dayMuscleGroup={day.muscleGroups.find(mg => mg.muscleGroupId === dex.muscleGroupId)}
            hasUserDismissed={hasUserDismissed}
            onClickMuscleGroup={handleOpenMuscleGroupOnly}
            onOpenRequiredFeedback={handleOpenRequiredFeedback}
            requiredFeedbackData={requiredFeedbackData}
            setDayExerciseToDelete={setDayExerciseToDelete}
          />
        ))}

        {shouldShowFinishWorkoutButton && (
          <div className="fixed bottom-0 w-full p-4 standalone:pb-safe-offset-4 desktop:static desktop:flex desktop:justify-end">
            <button
              disabled={finishWorkout.isWorking}
              className="btn btn-accent w-full shadow-lg desktop:-mr-4 desktop:-mt-4 desktop:w-auto"
              onClick={handleFinishWorkout}
            >
              {finishWorkout.isWorking && <span className="loading" />}
              Finish workout
            </button>
          </div>
        )}

        {shouldShowButton && (
          <div className="fixed bottom-0 w-full p-4 standalone:pb-safe-offset-4 desktop:static desktop:flex desktop:justify-end">
            <button
              disabled={finishMeso.isWorking}
              className="btn btn-accent w-full shadow-lg desktop:-mr-4 desktop:-mt-4 desktop:w-auto"
              onClick={handleFinishMeso}
            >
              {finishMeso.isWorking && <span className="loading" />}
              Finish mesocycle
            </button>
          </div>
        )}

        <MesoDayReadynessDialog
          bodyweight={day.bodyweight}
          onClose={handleCloseBodyweight}
          onSave={handleUpdateBodyweight}
          loading={updateBodyweight.isWorking}
          meso={meso}
          hasCompletedBodyweightSets={
            !!bodyweightDexes.find(
              ex =>
                STATUSES_FINISHED.exercise.includes(ex.status) ||
                ex.status === STATUSES.exercise.started
            )
          }
        />

        <FeedbackDialog
          day={day}
          feedbackDialogData={feedbackDialogData}
          requiredFeedbackData={requiredFeedbackData}
          onClose={handleCloseFeedback}
        />

        <ConfirmDialog
          isOpen={resetDayOpen}
          onClose={handleCloseResetDay}
          isLoading={resetDay.isWorking}
          onConfirm={handleResetDay}
          confirmButtonText="Reset day"
          description="This will reset all of the sets in this workout."
        />

        <ConfirmDialog
          title="End workout?"
          isOpen={skipMesoDayOpen}
          onClose={handleCloseSkipDay}
          isLoading={skipDay.isWorking}
          onConfirm={handleSkipDay}
          confirmButtonText="End workout"
          message="This cannot be undone"
          variant="error"
          confirmText="end workout"
          description="Ending this workout will skip all unfinished sets."
        />

        <DeleteMesoDayExerciseDialog
          dayId={day.id}
          dayExercise={dayExerciseToDelete}
          isOpen={!!dayExerciseToDelete}
          onClose={() => setDayExerciseToDelete()}
        />

        <RepsInfoDialog targetRIR={targetRIR} />

        <ProgressionsDialog
          isOpen={progressionsOpen}
          isSaving={updateProgressions.isWorking}
          onClose={() => setProgressionsOpen(false)}
          mutation={updateProgressions}
          progressions={meso?.progressions}
        />

        <MesoSummaryDialog
          meso={meso}
          isOpen={isMesoSummaryOpen}
          onClose={() => setIsMesoSummaryOpen(false)}
        />

        <PostWorkoutDialog
          onSummary={() => setIsMesoSummaryOpen(true)}
          handleFinishMeso={handleFinishMeso}
          isOpen={finishWorkoutDialogOpen}
          isWorking={finishMeso.isWorking}
          onClose={() => setFinishWorkoutDialogOpen(false)}
          day={day}
        />

        <PostMesoDialog isOpen={postMesoDialogOpen} onClose={() => setPostMesoDialogOpen(false)} />

        <ConfirmDialog
          title={dayInProgress ? 'Workout in progress' : 'End mesocycle?'}
          confirmButtonText={dayInProgress ? 'Ok' : 'End meso'}
          isOpen={endMesoOpen}
          onClose={handleEndMesoClose}
          onConfirm={dayInProgress ? handleEndMesoClose : handleFinishMeso}
          variant={dayInProgress ? 'info' : 'error'}
          confirmText={!dayInProgress ? 'end meso' : null}
          message={dayInProgress ? 'A workout is currently in progress' : 'This cannot be undone'}
        >
          {!dayInProgress ? (
            <p>Ending this mesocycle will mark all unfinished days as skipped.</p>
          ) : (
            <div>
              {isSameDay && <p>Please complete your workout before continuing.</p>}
              {!isSameDay && (
                <Fragment>
                  <p>
                    {`Please complete workout week ${dayInProgress.week + 1} day ${
                      dayInProgress.position + 1
                    } before continuing.`}
                  </p>
                  <button onClick={handleLink} className="mt-4 text-primary">
                    Take me there
                  </button>
                </Fragment>
              )}
            </div>
          )}
        </ConfirmDialog>
      </div>
    </Page>
  );
};

export default MesoDay;
